import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Heart, Leaf, Users, Phone, Mail, MapPin, Calendar, ArrowRight, Sparkles, Shield, BookOpen, MessageCircle, Brain, Target, Bed, Apple, Flower2, Home, Globe, Activity } from 'lucide-react';
import NavBar from '@/components/NavBar';
import { ReturnToTop } from '@/components/ReturnToTop';
import ScrollDownArrow from '@/components/ScrollDownArrow';
import { BOOKING_URL } from '@/constants/urls';
import heroImage from '@/assets/hero_image.jpg';

const PainInjuryPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = "Pain Relief Acupuncture Currimundi | Injury Recovery";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Natural pain relief and injury recovery with acupuncture in Currimundi. Treat back pain, joint issues, sports injuries & chronic pain. Book online or visit our Sunshine Coast clinic.");
    }
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Pain Relief Acupuncture Currimundi | Injury Recovery");
    }
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Natural pain relief and injury recovery with acupuncture in Currimundi. Treat back pain, joint issues, sports injuries & chronic pain. Book online or visit our Sunshine Coast clinic.");
    }
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/pain-injury");
    }
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', "https://womenandtheway.com.au/hero_image.jpg");
    }
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Pain Relief Acupuncture Currimundi | Injury Recovery");
    }
    const schema = {
      "@context": "https://schema.org",
      "@type": "MedicalClinic",
      "name": "Women and The Way - Acupuncture for Pain & Injury",
      "image": "https://womenandtheway.com.au/hero_image.jpg",
      "description": "Natural pain relief and injury recovery with acupuncture in Currimundi. Treat back pain, joint issues, sports injuries & chronic pain.",
      "url": "https://womenandtheway.com.au/pain-injury",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "700 Nicklin Way",
        "addressLocality": "Currimundi",
        "addressRegion": "QLD",
        "postalCode": "4551",
        "addressCountry": "AU"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -26.5270,
        "longitude": 153.0890
      },
      "priceRange": "$$",
      "telephone": "0415537784",
      "email": "womenandtheway@gmail.com",
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "17:00"
      },
      "serviceType": "Pain Relief, Injury Recovery, Acupuncture, Chinese Medicine, Musculoskeletal Treatment",
      "areaServed": {
        "@type": "Place",
        "name": "Sunshine Coast, Queensland, Australia"
      },
      "sameAs": [
        "https://www.facebook.com/Womenandtheway",
        "https://www.instagram.com/women_and_the_way"
      ]
    };
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) existingSchema.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, []);

  const handleBooking = async () => {
    setIsLoading(true);
    try {
      window.open(BOOKING_URL, '_blank');
    } catch (error) {
      console.error('Booking error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-white via-white to-amber-50">
        <NavBar />
        
        {/* Hero Section */}
        <section className="relative py-20 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                  Acupuncture for Pain & Injury
                </h1>
                <p className="text-xl md:text-2xl text-sage-600 mb-6 font-medium">
                  Relieve pain, restore movement, and recover with care.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  Pain affects everything - how you move, sleep, think, and feel. Whether it's a chronic condition or a new injury, acupuncture offers safe, effective relief without relying solely on medications. Our Currimundi clinic provides natural pain relief and injury recovery through Chinese Medicine, with online consultations available Australia-wide.
                </p>
                <Button 
                  size="lg" 
                  className="bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                  onClick={handleBooking}
                  disabled={isLoading}
                >
                  {isLoading ? 'Booking...' : 'Book Now'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <div className="relative">
                <img 
                  src={heroImage} 
                  alt="Acupuncture pain relief and injury recovery treatment at our Currimundi clinic on the Sunshine Coast" 
                  className="rounded-2xl shadow-lg w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Why Acupuncture for Pain */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6 flex items-center justify-center gap-3">
                <Target className="w-8 h-8 text-sage-600" />
                Why Acupuncture for Pain?
              </h2>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12">
              <p className="text-lg text-sage-700 mb-6 leading-relaxed">
                From a Chinese medicine perspective, pain is often caused by stagnation - of Qi (energy), Blood, or fluids. Acupuncture improves circulation, reduces inflammation, calms the nervous system, and restores the body's natural ability to repair tissue and regain function.
              </p>
              <p className="text-lg text-sage-700 leading-relaxed">
                At Women and the Way, we combine modern understanding of musculoskeletal health with Traditional Chinese Medicine to address the root cause of pain while supporting your body's natural healing response. Many of our clients also find benefits from our <a href="/stress-burnout" className="text-sage-600 hover:text-sage-800 underline">stress relief treatments</a> and <a href="/sleep-support" className="text-sage-600 hover:text-sage-800 underline">sleep support services</a>.
              </p>
            </div>
          </div>
        </section>

        {/* What We Treat */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                What We Treat
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Injuries & Musculoskeletal Pain */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Activity className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Injuries & Musculoskeletal Pain</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">Back pain (acute or chronic)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">Neck & shoulder tension</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">Knee, hip, or joint pain</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">Sprains, strains, or repetitive stress</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">Tendonitis, bursitis, frozen shoulder</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">TMJ or jaw pain</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">Sciatica & nerve-related pain</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Chronic Pain & Headaches */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Brain className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Chronic Pain & Headaches</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">Migraines & tension headaches</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">Fibromyalgia & chronic fatigue</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">Arthritis & joint inflammation</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">Post-surgical pain & recovery</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">Sports injuries & rehabilitation</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">Pelvic pain & dysfunction</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* What We Offer */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                What We Offer
              </h2>
              <p className="text-xl text-sage-600 mb-8">
                Pain Relief & Injury Recovery In-Clinic or Online
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* In-Clinic Care */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Sparkles className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Acupuncture</h3>
                  </div>
                  <p className="text-sage-700 mb-4">To reduce inflammation, improve circulation, and stimulate natural pain relief</p>
                  <div className="flex items-center gap-3 mb-4">
                    <Leaf className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Chinese Herbal Medicine</h3>
                  </div>
                  <p className="text-sage-700 mb-4">To support tissue repair, reduce inflammation, and strengthen the body</p>
                  <div className="flex items-center gap-3 mb-4">
                    <Heart className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Movement Assessment</h3>
                  </div>
                  <p className="text-sage-700 mb-4">To identify movement patterns and prevent future injuries</p>
                  <div className="flex items-center gap-3 mb-4">
                    <BookOpen className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Recovery Guidance</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Practical advice for safe movement and injury prevention</p>
                </CardContent>
              </Card>

              {/* Online Sessions */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <MessageCircle className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Telehealth Consultations</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Support for ongoing pain management and recovery guidance</p>
                  <div className="flex items-center gap-3 mb-4">
                    <Leaf className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Herbal Support</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Custom formulas for pain relief and tissue repair</p>
                  <div className="flex items-center gap-3 mb-4">
                    <BookOpen className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Movement Resources</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Guided exercises and movement practices for recovery</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
              Book Your Session
            </h2>
            <p className="text-xl text-sage-700 mb-8">
              Relieve pain, restore movement, and recover with care. Whether you prefer an in-person consultation at our Currimundi clinic or the convenience of an online telehealth session, we're here to support you.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <MapPin className="w-5 h-5 text-sage-600" />
                <a 
                  href="https://maps.google.com/?q=700+Nicklin+Way+Currimundi+QLD" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-sage-800 transition-colors"
                >
                  700 Nicklin Way, Currimundi
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Phone className="w-5 h-5 text-sage-600" />
                <a 
                  href="tel:0415537784" 
                  className="hover:text-sage-800 transition-colors"
                >
                  0415 537 784
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Mail className="w-5 h-5 text-sage-600" />
                <a 
                  href="mailto:womenandtheway@gmail.com" 
                  className="hover:text-sage-800 transition-colors"
                >
                  womenandtheway@gmail.com
                </a>
              </div>
            </div>
            <Button 
              size="lg" 
              className="w-full sm:w-auto text-center break-words bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 mx-auto"
              onClick={handleBooking}
              disabled={isLoading}
            >
              {isLoading ? 'Booking...' : 'Book Your Session'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </section>
        <ScrollDownArrow />
        <ReturnToTop />
      </div>
    </>
  );
};

export default PainInjuryPage; 